import { Container } from 'pixi.js';
import { gsap } from 'gsap';
import { FlagCard } from './flag-card.js';

export interface ArenaRegion {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * `grid` lays the field out as rows and columns - the tournament's qualifying
 * board. `disc` packs it into a circle, which is the battle-royale arena: the
 * flags visibly crowd each other, and as the field thins the survivors grow.
 */
export type ArenaLayout = 'grid' | 'disc';

interface Cell {
  readonly x: number;
  readonly y: number;
  readonly size: number;
}

/** Golden angle. Successive points never line up, so the disc looks packed. */
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

/**
 * Re-solving the layout is O(field size) in tweens, and the royale mode removes
 * flags eight times a second. Coalescing to a few times a second is invisible
 * on screen and keeps a 194-card arena from creating a thousand tweens a second.
 */
const RELAYOUT_INTERVAL = 0.22;

/**
 * The floor: every nation still in the tournament, as a grid.
 *
 * The grid re-solves itself whenever the field changes, so 194 cards fill the
 * frame edge to edge and 32 survivors fill exactly the same frame as large
 * cards. That continuous zoom is the phase's whole visual story - the field is
 * literally getting bigger as it gets smaller.
 */
export class Arena {
  readonly view = new Container();

  private readonly cards = new Map<string, FlagCard>();
  private order: string[] = [];
  private region: ArenaRegion = { x: 160, y: 250, width: 1600, height: 560 };
  private layout: ArenaLayout = 'grid';
  private maxCardWidth = 190;
  private relayoutTimer = 0;
  private relayoutPending = false;

  constructor() {
    this.view.eventMode = 'none';
  }

  get size(): number {
    return this.order.length;
  }

  setRegion(region: ArenaRegion): void {
    this.region = region;
  }

  /** `disc` also raises the ceiling on card size so a lone survivor fills it. */
  setLayout(layout: ArenaLayout, maxCardWidth = layout === 'disc' ? 340 : 190): void {
    this.layout = layout;
    this.maxCardWidth = maxCardWidth;
  }

  /** Coalesced re-layout. Call once per frame from the render loop. */
  update(deltaSeconds: number): void {
    if (!this.relayoutPending) return;

    this.relayoutTimer -= deltaSeconds;
    if (this.relayoutTimer > 0) return;

    this.relayoutPending = false;
    this.relayoutTimer = RELAYOUT_INTERVAL;
    this.relayout();
  }

  /** Replace the whole field. Cards not in `codes` are removed immediately. */
  setRoster(codes: readonly string[], animate = true): void {
    for (const [code, card] of this.cards) {
      if (!codes.includes(code)) {
        card.destroy({ children: true });
        this.cards.delete(code);
      }
    }

    this.order = [...codes];
    const layout = this.solve(this.order.length);

    this.order.forEach((code, index) => {
      let card = this.cards.get(code);
      const cell = layout[index]!;

      if (!card) {
        card = new FlagCard(code, { width: cell.size, showCode: cell.size >= 74 });
        this.cards.set(code, card);
        this.view.addChild(card);
        card.position.set(cell.x, cell.y);
        if (animate) card.enter(Math.min(2.4, index * 0.006));
      } else {
        this.place(card, cell, animate, index);
      }
    });
  }

  /** Blow up the given nations, then close the gaps they left behind. */
  eliminate(
    codes: readonly string[],
    onCardRemoved?: (x: number, y: number, card: FlagCard) => void,
  ): void {
    const removed: string[] = [];

    codes.forEach((code, i) => {
      const card = this.cards.get(code);
      if (!card) return;
      removed.push(code);
      onCardRemoved?.(card.x, card.y, card);
      card.eliminate(i * 0.035).eventCallback('onComplete', () => {
        card.destroy({ children: true });
      });
      this.cards.delete(code);
    });

    if (removed.length === 0) return;

    const gone = new Set(removed);
    this.order = this.order.filter((code) => !gone.has(code));

    // Down to the last few, every survivor growing is the moment - do it now
    // rather than waiting out the coalescing window.
    if (this.order.length <= 4) {
      this.relayoutPending = false;
      this.relayoutTimer = RELAYOUT_INTERVAL;
      this.relayout();
    } else {
      this.relayoutPending = true;
    }
  }

  /** Dim everything except `codes`. Used by the continent introductions. */
  spotlight(codes: readonly string[] | null): void {
    for (const [code, card] of this.cards) {
      const active = !codes || codes.includes(code);
      gsap.to(card, { alpha: active ? 1 : 0.12, duration: 0.6, ease: 'sine.out' });
      card.setHighlight(Boolean(codes) && active);
    }
  }

  cardFor(code: string): FlagCard | undefined {
    return this.cards.get(code);
  }

  /** World position of a nation's card, for aiming particles and the camera. */
  positionOf(code: string): { x: number; y: number } | null {
    const card = this.cards.get(code);
    return card ? { x: card.x, y: card.y } : null;
  }

  clear(): void {
    for (const card of this.cards.values()) card.destroy({ children: true });
    this.cards.clear();
    this.order = [];
  }

  private relayout(): void {
    const layout = this.solve(this.order.length);
    this.order.forEach((code, index) => {
      const card = this.cards.get(code);
      const cell = layout[index];
      if (card && cell) this.place(card, cell, true, index);
    });
  }

  private place(card: FlagCard, cell: Cell, animate: boolean, index: number): void {
    // Cards are built at a fixed pixel width, so re-fitting the grid is a scale
    // tween rather than a rebuild - rebuilding 194 sprites mid-phase stutters.
    const targetScale = cell.size / card.baseWidth;

    if (!animate) {
      card.position.set(cell.x, cell.y);
      card.scale.set(targetScale);
      return;
    }

    gsap.to(card, {
      x: cell.x,
      y: cell.y,
      duration: 0.9,
      ease: 'power3.inOut',
      delay: Math.min(0.5, index * 0.002),
      overwrite: 'auto',
    });
    gsap.to(card.scale, {
      x: targetScale,
      y: targetScale,
      duration: 0.9,
      ease: 'power3.inOut',
      delay: Math.min(0.5, index * 0.002),
      overwrite: 'auto',
    });
  }

  private solve(count: number): Cell[] {
    return this.layout === 'disc' ? this.solveDisc(count) : this.solveGrid(count);
  }

  /**
   * Phyllotaxis packing: `r ∝ √(i/n)`, `θ = i × golden angle`.
   *
   * Sunflower spacing. It fills a circle evenly at any count without a
   * relaxation pass, and because it is a pure function of the index the arena
   * stays visually stable as flags are removed - survivors drift inward and
   * grow rather than the whole field reshuffling on every knockout.
   */
  private solveDisc(count: number): Cell[] {
    if (count === 0) return [];

    const { x, y, width, height } = this.region;
    const cx = x + width / 2;
    const cy = y + height / 2;
    const radius = Math.min(width, height) / 2;

    // Area per flag, converted to a 4:3 card width, with slack so neighbours
    // breathe rather than tile.
    const cellArea = (Math.PI * radius * radius) / count;
    const size = Math.min(this.maxCardWidth, Math.sqrt(cellArea / 0.75) * 0.84);

    // Keep the outermost card fully inside the ring.
    const usable = Math.max(0, radius - size * 0.62);

    const cells: Cell[] = [];
    for (let i = 0; i < count; i++) {
      // The 0.5 offset stops a card sitting exactly on the centre at i = 0,
      // which would leave a visible hole once the ring around it filled in.
      const r = count === 1 ? 0 : usable * Math.sqrt((i + 0.5) / count);
      const theta = i * GOLDEN_ANGLE;
      cells.push({ x: cx + Math.cos(theta) * r, y: cy + Math.sin(theta) * r, size });
    }

    return cells;
  }

  /**
   * Choose the column count that yields the largest cards while still fitting
   * the region. Brute force over every candidate: at most 194 iterations of
   * arithmetic, run only when the field changes.
   */
  private solveGrid(count: number): Cell[] {
    if (count === 0) return [];

    const { x, y, width, height } = this.region;
    const gap = 0.18; // fraction of card width used as spacing

    let best = { columns: 1, size: 0 };
    for (let columns = 1; columns <= count; columns++) {
      const rows = Math.ceil(count / columns);
      const byWidth = width / (columns * (1 + gap));
      // Cards are 4:3 and carry a code label underneath.
      const byHeight = height / (rows * (0.75 + gap + 0.16));
      const size = Math.min(byWidth, byHeight);
      if (size > best.size) best = { columns, size };
    }

    const columns = best.columns;
    const size = Math.min(best.size, this.maxCardWidth);
    const rows = Math.ceil(count / columns);
    const stepX = size * (1 + gap);
    const stepY = size * (0.75 + gap + 0.16);

    const totalWidth = columns * stepX;
    const totalHeight = rows * stepY;
    const originX = x + (width - totalWidth) / 2 + stepX / 2;
    const originY = y + (height - totalHeight) / 2 + stepY / 2;

    const cells: Cell[] = [];
    for (let i = 0; i < count; i++) {
      const column = i % columns;
      const row = Math.floor(i / columns);
      // Centre the final, partly filled row.
      const rowCount = row === rows - 1 ? count - row * columns : columns;
      const rowOffset = ((columns - rowCount) * stepX) / 2;
      cells.push({
        x: originX + column * stepX + rowOffset,
        y: originY + row * stepY,
        size,
      });
    }

    return cells;
  }
}
